import {
	Shopper,
	OrderProduct,
	ShopperDataForContractEmail,
	ProductDataForContractEmail,
	Category,
	Languages,
	DateFormatObject,
	UserDetailName,
	UserProperties,
} from 'common/types';
import {
	getTotalRentalDurationFromProducts,
	getLatestProductEndTime,
	getDurationString,
} from 'common/utils/dateCalculations';
import { getTranslation } from 'common/modules/translations';
import { localFormat } from 'common/utils/dateUtils';
import { getDetailDisplayLabel, getLocalInputFromUserProperty } from 'common/utils/userDetails';
import { TFunction } from 'i18next';

export const createShopperListForEmail = (
	shoppers: Shopper[],
	products: OrderProduct[],
	categories: Category[],
	dateFormat: DateFormatObject,
	t: TFunction,
	lang?: Languages,
): ShopperDataForContractEmail[] => {
	return shoppers.map((shopper) => {
		const { firstName, lastName, segment, categoryIds, id } = shopper;
		const shopperProducts = products.filter((p) => p.shopperId === id);
		const { weight, height, shoeSize, experienceLevel } = getUserLocalProperties(
			shopper.userProperties,
			t,
		);
		const endDate = getLatestProductEndTime(shopperProducts);
		const shopper_end_date = endDate
			? localFormat(endDate, 'ddd D.M., HH:mm', dateFormat)
			: undefined;

		return {
			name: `${firstName} ${lastName}`,
			age_group: segment,
			weight,
			height,
			foot_size: shoeSize,
			experience_level: experienceLevel,
			rental_duration: getDurationString(
				getTotalRentalDurationFromProducts(shopperProducts),
				'long',
				lang,
				t,
			),
			products: createProductListForEmail(shopperProducts, lang),
			end_date: shopper_end_date,
			activity: getCategoryNameFromId(categoryIds?.[0] ?? '', categories, lang || 'en'),
		};
	});
};

const getUserLocalProperties = (_userProperties: UserProperties, t: TFunction) => {
	return Object.keys(_userProperties).reduce((userProperties: { [K: string]: string }, key) => {
		const detail = key as UserDetailName;
		const localInput = getLocalInputFromUserProperty(detail, _userProperties[detail]);
		const propertyValue = getDetailDisplayLabel(detail, localInput, t);
		userProperties[detail] = propertyValue;
		return userProperties;
	}, {});
};

const createProductListForEmail = (
	products: OrderProduct[],
	lang: Languages | undefined,
): ProductDataForContractEmail[] =>
	products.map((product) => ({
		name: getTranslation(product.name, lang || 'en'),
		additional_products: [],
	}));

const getCategoryNameFromId = (id: string, categories: Category[], lang: Languages) => {
	const selectedCategory = categories.filter((c) => c.id === id)[0];
	if (!selectedCategory) {
		return '';
	}
	return getTranslation(selectedCategory.name, lang);
};
